import React from "react";
import { Box } from "@mui/material";
import { configGrafico, PlotAcotado } from "./plotConfig";

const SERIE_1 = "#2a78d6";
const SERIE_2 = "#eb6834";
const SERIE_3 = "#1baf7a";
const CAJA_RELLENO = "#cde2fb";

const ESTADO_BUENO = "#0ca30c";
const ESTADO_ADVERTENCIA = "#fab219";
const ESTADO_CRITICO = "#d03b3b";
const GRIS_FALTANTE = "#9e9e9e";

const SECUENCIAL_AZUL = [
  [0, "#cde2fb"],
  [0.17, "#9ec5f4"],
  [0.34, "#6da7ec"],
  [0.5, "#3987e5"],
  [0.67, "#256abf"],
  [0.84, "#184f95"],
  [1, "#0d366b"],
];

const TINTA = "#0b0b0b";
const TINTA_SEC = "#52514e";
const TINTA_TENUE = "#898781";
const REJILLA = "#e1e0d9";
const SUPERFICIE = "#ffffff";

const FUENTE_TITULO = { size: 12, color: TINTA, family: "inherit" };
const FUENTE_EJE = { size: 10, color: TINTA_TENUE, family: "inherit" };
const FUENTE_ETIQUETA = { size: 10, color: TINTA_SEC, family: "inherit" };

const COMPOSICION = [
  { campo: "correctos", nombre: "Correctos", color: ESTADO_BUENO, patron: "" },
  { campo: "anomalos", nombre: "Anómalos", color: ESTADO_ADVERTENCIA, patron: "/" },
  { campo: "invalidos", nombre: "Inválidos", color: ESTADO_CRITICO, patron: "x" },
  { campo: "faltantes", nombre: "No recibidos", color: GRIS_FALTANTE, patron: "." },
];

const DESCUENTOS = [
  { flag: "Dd", nombre: "Sin dato (Dd)", color: ESTADO_CRITICO, patron: "x" },
  { flag: "Dl", nombre: "Latencia (Dl)", color: SERIE_1, patron: "" },
  { flag: "Dp", nombre: "Pérdida (Dp)", color: SERIE_2, patron: "/" },
  { flag: "Dprop", nombre: "Fuera de servicio (Dprop)", color: SERIE_3, patron: "." },
];

const VELOCIDADES = ["bajada", "subida", "interac_bajada"];

function fmt(valor, sufijo = "", decimales = null) {
  if (valor == null) return "-";
  const numero = Number(valor);
  let d = decimales;
  if (d == null) {
    if (Number.isInteger(numero)) d = 0;
    else if (Math.abs(numero) >= 100) d = 0;
    else if (Math.abs(numero) >= 10) d = 1;
    else d = 2;
  }
  return (
    numero.toLocaleString("es-CL", {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }) + sufijo
  );
}

function recortar(texto, largo = 24) {
  const valor = String(texto ?? "");
  return valor.length <= largo ? valor : `${valor.slice(0, largo - 1)}…`;
}

function nombreOperador(fila) {
  return fila?.nombre_operador || `Operador ${fila?.id_operador}`;
}

function altoBarras(filas) {
  return Math.max(150, 34 * filas + 96);
}

function altoCajas(filas) {
  return Math.max(170, 40 * filas + 96);
}

function layoutBase(titulo, alto, extra = {}) {
  return {
    title: {
      text: titulo,
      font: FUENTE_TITULO,
      x: 0,
      xanchor: "left",
      y: 0.97,
      yanchor: "top",
    },
    height: alto,
    margin: { l: 10, r: 60, t: 42, b: 34 },
    paper_bgcolor: SUPERFICIE,
    plot_bgcolor: SUPERFICIE,
    showlegend: false,
    bargap: 0.35,
    font: { family: "inherit" },
    hoverlabel: { bgcolor: SUPERFICIE, bordercolor: REJILLA, font: FUENTE_ETIQUETA },
    xaxis: {
      gridcolor: REJILLA,
      zeroline: false,
      tickfont: FUENTE_EJE,
      automargin: true,
      separatethousands: true,
    },
    yaxis: {
      tickfont: FUENTE_ETIQUETA,
      automargin: true,
      ticks: "",
      showgrid: false,
      autorange: "reversed",
    },
    ...extra,
  };
}

function layoutVertical(titulo, alto, extra = {}) {
  return {
    ...layoutBase(titulo, alto, extra),
    yaxis: {
      gridcolor: REJILLA,
      zeroline: false,
      tickfont: FUENTE_EJE,
      automargin: true,
      separatethousands: true,
      ...(extra.yaxis || {}),
    },
    xaxis: {
      tickfont: FUENTE_EJE,
      automargin: true,
      showgrid: false,
      type: "category",
      ...(extra.xaxis || {}),
    },
  };
}

function ejeCategorias(etiquetas) {
  return {
    categoryorder: "array",
    categoryarray: etiquetas,
    tickfont: FUENTE_ETIQUETA,
    automargin: true,
    ticks: "",
    showgrid: false,
    autorange: "reversed",
  };
}

const LEYENDA_INFERIOR = {
  orientation: "h",
  yanchor: "top",
  y: -0.14,
  xanchor: "center",
  x: 0.5,
  font: FUENTE_ETIQUETA,
};

function Grafico({ data, layout, nombre, alto }) {
  return (
    <PlotAcotado
      data={data}
      layout={layout}
      config={configGrafico(["reporte_calidad", nombre], { alto })}
      useResizeHandler
      style={{ width: "100%", height: "100%" }}
    />
  );
}

function Envoltura({ children }) {
  return <Box sx={{ px: 1, pb: 1 }}>{children}</Box>;
}

function Paneles({ children, columnas = 2 }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: `repeat(${columnas}, 1fr)` },
        gap: 1,
        px: 1,
        pb: 1,
      }}
    >
      {children}
    </Box>
  );
}

function barrasSimples(etiquetas, valores, color, sufijo, decimales) {
  return [
    {
      type: "bar",
      orientation: "h",
      y: etiquetas.map((e) => recortar(e)),
      x: valores,
      marker: { color },
      text: valores.map((v) => fmt(v, sufijo, decimales)),
      textposition: "outside",
      textfont: FUENTE_ETIQUETA,
      cliponaxis: false,
      hovertemplate: "%{y}: %{text}<extra></extra>",
    },
  ];
}

function trazasCaja(etiquetas, resumenes) {
  const conDatos = resumenes
    .map((resumen, indice) => ({ resumen, indice }))
    .filter(({ resumen }) => resumen && resumen.mediana != null && resumen.q1 != null);

  if (!conDatos.length) return null;

  const trazas = [
    {
      type: "box",
      orientation: "h",
      y: conDatos.map(({ indice }) => etiquetas[indice]),
      q1: conDatos.map(({ resumen }) => resumen.q1),
      median: conDatos.map(({ resumen }) => resumen.mediana),
      q3: conDatos.map(({ resumen }) => resumen.q3),
      lowerfence: conDatos.map(({ resumen }) => resumen.bigote_inferior),
      upperfence: conDatos.map(({ resumen }) => resumen.bigote_superior),
      fillcolor: CAJA_RELLENO,
      line: { color: SERIE_1, width: 1.2 },
      marker: { color: SERIE_1 },
      hoverinfo: "x+y",
      showlegend: false,
    },
  ];

  const atipicosX = [];
  const atipicosY = [];
  conDatos.forEach(({ resumen, indice }) => {
    (resumen.atipicos || []).forEach((valor) => {
      atipicosX.push(valor);
      atipicosY.push(etiquetas[indice]);
    });
  });

  if (atipicosX.length) {
    trazas.push({
      type: "scatter",
      mode: "markers",
      x: atipicosX,
      y: atipicosY,
      marker: { color: SERIE_1, size: 6, opacity: 0.45 },
      hovertemplate: "%{x}<extra>fuera de norma</extra>",
      showlegend: false,
    });
  }

  return trazas;
}

function lineaVertical(valor) {
  return {
    type: "line",
    x0: valor,
    x1: valor,
    yref: "paper",
    y0: 0,
    y1: 1,
    line: { color: TINTA_TENUE, width: 1, dash: "dash" },
  };
}

function PanelCajas({ titulo, etiquetas, resumenes, nombre, referencia = null }) {
  const trazas = trazasCaja(etiquetas, resumenes);
  if (!trazas) return null;

  const alto = altoCajas(etiquetas.length);
  const layout = layoutBase(titulo, alto, {
    yaxis: ejeCategorias(etiquetas),
    margin: { l: 10, r: 40, t: 42, b: 34 },
  });
  if (referencia != null) layout.shapes = [lineaVertical(referencia)];

  return <Grafico data={trazas} layout={layout} nombre={nombre} alto={alto} />;
}

function apiladas(etiquetas, series, sufijo, decimales) {
  return series.map((serie) => ({
    type: "bar",
    orientation: "h",
    name: serie.nombre,
    y: etiquetas,
    x: serie.valores,
    marker: {
      color: serie.color,
      pattern: {
        shape: serie.patron,
        fillmode: "overlay",
        fgcolor: SUPERFICIE,
        size: 4,
        solidity: 0.3,
      },
    },
    text: serie.valores.map((v) => fmt(v, sufijo, decimales)),
    textposition: "none",
    hovertemplate: `%{y}: %{text}<extra>${serie.nombre}</extra>`,
  }));
}

export function GraficoCobertura({ reporte }) {
  const dias = reporte?.cobertura?.por_dia || [];
  if (dias.length < 2) return null;

  const parque = reporte?.cobertura?.rbd_parque || 0;
  const valores = dias.map((d) => d.rbd_calculados || 0);
  if (!valores.some((v) => v)) return null;

  const alto = 260;
  const layout = layoutVertical("RBD calculados por día del período", alto, {
    xaxis: { title: { text: "Día del mes", font: FUENTE_EJE, standoff: 6 } },
    margin: { l: 50, r: 30, t: 42, b: 50 },
  });
  if (parque) {
    layout.shapes = [
      {
        type: "line",
        xref: "paper",
        x0: 0,
        x1: 1,
        y0: parque,
        y1: parque,
        line: { color: TINTA_TENUE, width: 1, dash: "dash" },
      },
    ];
  }

  return (
    <Envoltura>
      <Grafico
        data={[
          {
            type: "bar",
            x: dias.map((d) => d.fecha.slice(-2)),
            y: valores,
            marker: {
              color: valores.map((v) =>
                parque && v >= parque * 0.99
                  ? ESTADO_BUENO
                  : v
                  ? ESTADO_ADVERTENCIA
                  : ESTADO_CRITICO
              ),
            },
            hovertemplate: "Día %{x}: %{y:,} RBD<extra></extra>",
          },
        ]}
        layout={layout}
        nombre="cobertura"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoSerieDiaria({ reporte }) {
  const serie = reporte?.serie_diaria || [];
  if (serie.filter((s) => s.porcentaje_recepcion != null).length < 2) return null;

  const alto = 280;
  const layout = layoutVertical(
    "Recepción y datos correctos por día (% de lo exigible)",
    alto,
    {
      xaxis: { title: { text: "Día del mes", font: FUENTE_EJE, standoff: 6 } },
      yaxis: { range: [0, 105], ticksuffix: " %" },
      margin: { l: 50, r: 30, t: 42, b: 62 },
    }
  );
  layout.barmode = "overlay";
  layout.showlegend = true;
  layout.legend = LEYENDA_INFERIOR;

  const dias = serie.map((s) => s.fecha.slice(-2));

  return (
    <Envoltura>
      <Grafico
        data={[
          {
            type: "bar",
            name: "Recibidos",
            x: dias,
            y: serie.map((s) => s.porcentaje_recepcion || 0),
            marker: { color: CAJA_RELLENO },
            hovertemplate: "Día %{x}: %{y:.2f} % recibido<extra></extra>",
          },
          {
            type: "bar",
            name: "Correctos",
            x: dias,
            y: serie.map((s) => s.porcentaje_conforme || 0),
            marker: { color: SERIE_1 },
            hovertemplate: "Día %{x}: %{y:.2f} % correcto<extra></extra>",
          },
        ]}
        layout={layout}
        nombre="serie_diaria"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoRecepcionPorOperador({ reporte }) {
  const filas = reporte?.por_operador || [];
  if (filas.length < 2) return null;

  const etiquetas = filas.map(nombreOperador);
  const paneles = [
    {
      titulo: "Recepción (% de slots exigibles)",
      valores: filas.map((f) => f.porcentaje_recepcion || 0),
      color: SERIE_1,
    },
    {
      titulo: "Integridad (% correctos de lo recibido)",
      valores: filas.map((f) => f.porcentaje_integridad || 0),
      color: SERIE_3,
    },
  ];

  const alto = altoBarras(filas.length);

  return (
    <Paneles>
      {paneles.map((panel) => (
        <Grafico
          key={panel.titulo}
          data={barrasSimples(etiquetas, panel.valores, panel.color, " %", 2)}
          layout={layoutBase(panel.titulo, alto, { yaxis: ejeCategorias(etiquetas) })}
          nombre={panel.titulo}
          alto={alto}
        />
      ))}
    </Paneles>
  );
}

export function GraficoComposicion({ reporte }) {
  const filas = reporte?.por_operador || [];
  if (!filas.length) return null;

  const etiquetas = filas.map(nombreOperador);
  const series = COMPOSICION.map((serie) => ({
    ...serie,
    valores: filas.map((f) => f[serie.campo] || 0),
  })).filter((serie) => serie.valores.some((v) => v));

  if (!series.length) return null;

  const alto = altoBarras(filas.length) + 40;
  const layout = layoutBase("Composición de los slots exigibles", alto, {
    yaxis: ejeCategorias(etiquetas),
    barmode: "stack",
    showlegend: true,
    legend: LEYENDA_INFERIOR,
    margin: { l: 10, r: 40, t: 42, b: 62 },
  });

  return (
    <Envoltura>
      <Grafico
        data={apiladas(etiquetas, series, " slots")}
        layout={layout}
        nombre="composicion"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoComposicionRelativa({ reporte }) {
  const filas = (reporte?.por_operador || []).filter((f) => f.total);
  if (!filas.length) return null;

  const etiquetas = filas.map(nombreOperador);
  const series = COMPOSICION.map((serie) => ({
    ...serie,
    valores: filas.map((f) => ((f[serie.campo] || 0) / f.total) * 100),
  })).filter((serie) => serie.valores.some((v) => v));

  if (!series.length) return null;

  const alto = altoBarras(filas.length) + 40;
  const layout = layoutBase(
    "Composición relativa: % de los slots exigibles de cada operador",
    alto,
    {
      yaxis: ejeCategorias(etiquetas),
      barmode: "stack",
      showlegend: true,
      legend: LEYENDA_INFERIOR,
      margin: { l: 10, r: 40, t: 42, b: 62 },
      xaxis: {
        gridcolor: REJILLA,
        zeroline: false,
        tickfont: FUENTE_EJE,
        automargin: true,
        range: [0, 100],
        ticksuffix: " %",
      },
    }
  );

  return (
    <Envoltura>
      <Grafico
        data={apiladas(etiquetas, series, " %", 2)}
        layout={layout}
        nombre="composicion_relativa"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoRecepcionPorRbd({ reporte }) {
  const filas = (reporte?.por_operador || []).filter(
    (f) => f.recepcion_por_rbd?.mediana != null
  );
  if (!filas.length) return null;

  return (
    <Envoltura>
      <PanelCajas
        titulo="Recepción por establecimiento (%), dentro de cada operador"
        etiquetas={filas.map(nombreOperador)}
        resumenes={filas.map((f) => f.recepcion_por_rbd)}
        nombre="recepcion_por_rbd"
      />
    </Envoltura>
  );
}

export function GraficoIntegridadPorMetrica({ reporte }) {
  const filas = (reporte?.integridad?.por_metrica || []).filter(
    (f) => f.porcentaje_sentinela != null
  );
  if (filas.length < 2) return null;

  const etiquetas = filas.map((f) => f.nombre);
  const valores = filas.map((f) => f.porcentaje_sentinela);
  const alto = altoBarras(filas.length);

  return (
    <Envoltura>
      <Grafico
        data={barrasSimples(etiquetas, valores, ESTADO_CRITICO, " %", 2)}
        layout={layoutBase(
          "Mediciones sin dato válido, por métrica (%)",
          alto,
          { yaxis: ejeCategorias(etiquetas) }
        )}
        nombre="integridad_por_metrica"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoEnviosExtra({ reporte }) {
  const filas = (reporte?.envios_extra?.por_operador || []).filter((f) => f.extra);
  if (!filas.length) return null;

  const etiquetas = filas.map(nombreOperador);
  const paneles = [
    {
      titulo: "Mediciones de más",
      valores: filas.map((f) => f.extra || 0),
      color: SERIE_2,
      sufijo: "",
      decimales: 0,
    },
    {
      titulo: "% sobre los slots reportados",
      valores: filas.map((f) => f.porcentaje_extra || 0),
      color: SERIE_1,
      sufijo: " %",
      decimales: 2,
    },
  ];
  const alto = altoBarras(filas.length);

  return (
    <Paneles>
      {paneles.map((panel) => (
        <Grafico
          key={panel.titulo}
          data={barrasSimples(
            etiquetas, panel.valores, panel.color, panel.sufijo, panel.decimales
          )}
          layout={layoutBase(panel.titulo, alto, { yaxis: ejeCategorias(etiquetas) })}
          nombre={panel.titulo}
          alto={alto}
        />
      ))}
    </Paneles>
  );
}

export function GraficoVelocidadGlobal({ reporte }) {
  const corte = reporte?.velocidad?.global || {};
  const paneles = VELOCIDADES.map((metrica) => corte[metrica]).filter(
    (datos) => datos?.ratio?.mediana != null
  );
  if (!paneles.length) return null;

  return (
    <Paneles columnas={paneles.length}>
      {paneles.map((panel) => (
        <PanelCajas
          key={panel.nombre}
          titulo={panel.nombre}
          etiquetas={[""]}
          resumenes={[panel.ratio]}
          nombre={`velocidad_${panel.nombre}`}
          referencia={100}
        />
      ))}
    </Paneles>
  );
}

export function GraficoVelocidadPorOperador({ reporte, metrica = "bajada" }) {
  const filas = (reporte?.velocidad?.por_operador || []).filter(
    (f) => f[metrica]?.ratio?.mediana != null
  );
  if (!filas.length) return null;

  const nombre = filas[0][metrica]?.nombre || metrica;

  return (
    <Envoltura>
      <PanelCajas
        titulo={`${nombre}: velocidad medida sobre la contratada (%)`}
        etiquetas={filas.map(nombreOperador)}
        resumenes={filas.map((f) => f[metrica].ratio)}
        nombre={`velocidad_operador_${metrica}`}
        referencia={100}
      />
    </Envoltura>
  );
}

export function GraficoDescuentos({ reporte }) {
  const filas = reporte?.descuentos?.por_operador || [];
  if (!filas.length) return null;

  const etiquetas = filas.map(nombreOperador);
  const series = DESCUENTOS.map((serie) => ({
    ...serie,
    valores: filas.map((f) => f[`perc_${serie.flag}`] || 0),
  })).filter((serie) => serie.valores.some((v) => v));

  if (!series.length) return null;

  const alto = altoBarras(filas.length) + 40;
  const layout = layoutBase(
    "Slots con causal de descuento, sobre la ventana (%)",
    alto,
    {
      yaxis: ejeCategorias(etiquetas),
      barmode: "stack",
      showlegend: true,
      legend: LEYENDA_INFERIOR,
      margin: { l: 10, r: 40, t: 42, b: 62 },
    }
  );

  return (
    <Envoltura>
      <Grafico
        data={apiladas(etiquetas, series, " %", 2)}
        layout={layout}
        nombre="descuentos"
        alto={alto}
      />
    </Envoltura>
  );
}

export function GraficoMapaCalor({ reporte }) {
  const mapa = reporte?.mapa_calor;
  const filas = mapa?.filas || [];
  const dias = mapa?.dias || [];
  if (!filas.length || !dias.length) return null;

  const dimension = mapa.dimension === "zona" ? "zona" : "operador";
  const alto = Math.max(220, 26 * filas.length + 140);
  const layout = layoutBase(
    `Slots no recibidos por día y ${dimension} (%)`,
    alto,
    {
      margin: { l: 10, r: 20, t: 42, b: 50 },
      xaxis: {
        title: { text: "Día del mes", font: FUENTE_EJE, standoff: 6 },
        tickfont: FUENTE_EJE,
        type: "category",
        showgrid: false,
        automargin: true,
      },
      yaxis: {
        tickfont: FUENTE_ETIQUETA,
        automargin: true,
        ticks: "",
        showgrid: false,
        autorange: "reversed",
        type: "category",
      },
    }
  );

  return (
    <Envoltura>
      <Grafico
        data={[
          {
            type: "heatmap",
            x: dias.map((d) => String(d)),
            y: filas.map((f) => recortar(f.nombre_zona, 18)),
            z: filas.map((f) => f.valores.map((v) => (v > 0 ? v : null))),
            text: filas.map((f) => f.valores.map((v) => fmt(v, " %", 2))),
            colorscale: SECUENCIAL_AZUL,
            zmin: 0,
            zmax: Math.max(mapa.maximo || 1, 1),
            xgap: 1,
            ygap: 1,
            hoverongaps: false,
            colorbar: {
              title: { text: "% no recibido", font: FUENTE_ETIQUETA, side: "right" },
              thickness: 10,
              outlinewidth: 0,
              tickfont: FUENTE_EJE,
              ticksuffix: " %",
            },
            hovertemplate: "%{y} · día %{x}: %{text}<extra></extra>",
          },
        ]}
        layout={layout}
        nombre="mapa_calor"
        alto={alto}
      />
    </Envoltura>
  );
}
